/**
 * Mise en forme — français, et rien que de l'affichage.
 *
 * Les conversions d'horloge passent par les constantes du moteur (`H_JOUR`…) : SPEC-MOTEUR
 * §2.1 le demande explicitement (« pour que personne ne réécrive `/ 16` »), et une constante
 * recopiée dérive.
 */

import { H_EVEILLE_PAR_JOUR, H_JOUR, jour as jourMoteur } from "@maxime/shared";
import type { AtelierStage, VideoClass } from "@maxime/shared";

const FR = new Intl.NumberFormat("fr-FR");

export function nombre(v: number): string {
  return FR.format(Math.round(v));
}

/**
 * ⟦C62 · friction 6⟧ **Des points.** « 1 point », « 2 points » : le sigle « PC » se lisait « ordinateur » (la pièce du
 * joueur en a un, sur lequel il clique pour publier). Jamais le sigle à l'écran ; les identifiants techniques
 * (`state.pc`, `pc_gagnes`, `not_enough_pc`) restent ce qu'ils sont. Le singulier couvre 0 et 1 (usage français).
 */
export function points(n: number): string {
  const v = Math.round(n);
  return `${nombre(v)} point${Math.abs(v) >= 2 ? "s" : ""}`;
}

/** Abrégé pour les gros compteurs : 12 408 → « 12,4 k ». */
export function nombreCourt(v: number): string {
  if (v < 10_000) return nombre(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 100_000 ? 1 : 0).replace(".", ",")} k`;
  return `${(v / 1_000_000).toFixed(2).replace(".", ",")} M`;
}

/**
 * Le nombre **gravé sur une plaque** — « 100 k », « 1 M », « 10 M ».
 *
 * `nombreCourt` garde deux décimales au-dessus du million (« 1,00 M »), ce qui est juste pour
 * un compteur qui bouge, et faux pour une plaque : un palier est un nombre rond par
 * construction, et une plaque de 50 px n'a la place ni des décimales ni des espaces.
 */
export function nombrePlaque(v: number): string {
  if (v >= 1_000_000) return `${nombre(v / 1_000_000)} M`;
  if (v >= 1_000) return `${nombre(v / 1_000)} k`;
  return nombre(v);
}

/** Jour simulé courant (§2.1). */
export function jourSimule(clockH: number): number {
  return jourMoteur(clockH);
}

/**
 * Heure **de calendrier** dans le jour courant — « J12, 6 h ». ⟦A-19⟧ Depuis que la nuit coûte
 * `H_NUIT`, `clock_h` compte les 24 h du jour, pas les seules heures éveillées : le reste modulo
 * `H_JOUR` est donc une heure de la journée, nuit comprise.
 */
export function heureDuJour(clockH: number): number {
  return clockH % H_JOUR;
}

/** Zone de santé mentale (SPEC-UI §7.5) — pilote une classe sur la racine de l'app. */
export type ZoneSM = "vert" | "fatigue" | "rouge" | "crise";

export function zoneSM(mental: number): ZoneSM {
  if (mental <= 0) return "crise";
  if (mental < 30) return "rouge";
  if (mental < 60) return "fatigue";
  return "vert";
}

/** Bande d'alignement en toutes lettres (SPEC-UI §8.1 : ☀️ lumière / ⚖ neutre / 🌀 chaos). */
export function badgeAlignement(alignement: number): { icone: string; libelle: string } {
  if (alignement >= 30) return { icone: "☀️", libelle: "lumière" };
  if (alignement <= -30) return { icone: "🌀", libelle: "chaos" };
  return { icone: "⚖", libelle: "neutre" };
}

/** Classes de vidéo (SPEC-UI §8.1). */
export const BADGE_CLASSE: Record<VideoClass, { icone: string; libelle: string }> = {
  trend: { icone: "🔥", libelle: "En tendance" },
  standard: { icone: "▶", libelle: "Standard" },
  evergreen: { icone: "🌲", libelle: "Intemporelle" },
  nulle: { icone: "💀", libelle: "Nulle" },
};

/** Étapes de l'Atelier — libellés et icônes (SPEC-UI §3.3). */
export const ETAPES: Record<AtelierStage, { nom: string; icone: string; verbe: string }> = {
  ecriture: { nom: "Écriture", icone: "✍️", verbe: "Arranger" },
  tournage: { nom: "Tournage", icone: "🎥", verbe: "Jouer" }, // ⟦C53⟧ « La prise parfaite » (ex-« Cadrer »)
  montage: { nom: "Montage", icone: "✂️", verbe: "Rythmer" },
  miniature: { nom: "Miniature + Titre", icone: "🖼️", verbe: "Doser" },
  promotion: { nom: "Promotion", icone: "📣", verbe: "Parier" },
};

/**
 * ⟦C15 · A-135 · A-136⟧ **Nuit simulée**, pour la variante `-nuit` des planches de chambre
 * (`assets/img-src/chambre/chambre-nX-{jour,nuit}.png`). Dérivé de `clock_h`, aucun nouvel
 * état : `heureDuJour` découpe déjà le cycle de `H_JOUR` (24 h) en heures de calendrier — la
 * nuit en occupe les `H_NUIT` (8) dernières, une fois les `H_EVEILLE_PAR_JOUR` (16) heures de
 * travail écoulées (⟦A-19⟧, `clock.ts`). Remplace les deux anciens placeholders emoji
 * (`avatarPlaceholder`, `chambrePlaceholder`, §8.1) : morts depuis le reskin 8-bit ⟦A-133 ·
 * A-134⟧ (zéro appelant, vérifié avant suppression), et c'est exactement le genre de
 * placeholder que ce chantier retire.
 */
export function estNuit(clockH: number): boolean {
  return heureDuJour(clockH) >= H_EVEILLE_PAR_JOUR;
}

/**
 * « 3 h · −12 ⚡ · −1 idée » : le coût affiché DANS le bouton (SPEC-UI §2).
 *
 * ⟦C62 · lot événements · A-179⟧ Les idées s'écrivent avec leur MOT, jamais avec le pictogramme de la carte : il ne
 * disait à personne ce qu'on dépense (audit du 21/10 : la ressource n'est nommée nulle part avant la fenêtre
 * « Nouvelle vidéo »). Un seul mot pour la même chose partout : « idée ».
 */
export function libelleCout(cout: {
  hours: number;
  energy_delta: number;
  money_delta: number;
  mental_delta: number;
  cards_consumed: number;
  cards_drawn: number;
}): string {
  const bouts: string[] = [];
  if (cout.hours > 0) bouts.push(`${nombre(cout.hours)} h`);
  if (cout.energy_delta !== 0) {
    bouts.push(`${cout.energy_delta > 0 ? "+" : "−"}${Math.abs(Math.round(cout.energy_delta))} ⚡`);
  }
  if (cout.mental_delta !== 0) {
    bouts.push(`${cout.mental_delta > 0 ? "+" : "−"}${Math.abs(Math.round(cout.mental_delta * 10) / 10)} ❤`);
  }
  if (cout.money_delta !== 0) {
    bouts.push(`${cout.money_delta > 0 ? "+" : "−"}${nombre(Math.abs(cout.money_delta))} ₵`);
  }
  if (cout.cards_consumed > 0) bouts.push(`−${cout.cards_consumed} ${cout.cards_consumed > 1 ? "idées" : "idée"}`);
  if (cout.cards_drawn > 0) bouts.push(`+${cout.cards_drawn} ${cout.cards_drawn > 1 ? "idées" : "idée"}`);
  return bouts.join(" · ");
}
